import {ref} from 'vue'
import {BASE_URL} from "@/composables/url_config";
import {log} from "@/composables/useLog"
import axios from "axios";

const error = ref(null)

const getHeaders = (token) => ({
    "Authorization": `Bearer ${token}`,
    "Content-Type": "application/json",
    "Accept": "application/json"
})

export const getTrafficSummary = async (token, { simId, clientId, periodType, startDate, endDate, networkType, imsiGroupId } = {}) => {
    log("welcome in getTrafficSummary")
    if (!token || token === "") return { error: true, message: "No token provided", summaries: [] }

    try {
        const params = new URLSearchParams()
        if (simId) params.append('sim_id', simId)
        if (clientId) params.append('client_id', clientId)
        if (periodType) params.append('period_type', periodType)
        if (startDate) params.append('start_date', startDate)
        if (endDate) params.append('end_date', endDate)
        if (networkType) params.append('network_type', networkType)
        if (imsiGroupId) params.append('imsi_group_id', imsiGroupId)

        const url = `${BASE_URL}/home/get_traffic_summary_api?${params.toString()}`
        const result = await axios.get(url, { headers: getHeaders(token) })
        return result.data
    } catch (err) {
        log("Error in getTrafficSummary")
        error.value = err.message
        return { error: true, message: "Eroare la getTrafficSummary", summaries: [] }
    }
}

export const getTrafficDetails = async (token, { simId, clientId, recordType, search, startDate, endDate, page, perPage, imsiGroupId } = {}) => {
    log("welcome in getTrafficDetails")
    if (!token || token === "") return { error: true, message: "No token provided", records: [] }

    try {
        const params = new URLSearchParams()
        if (simId) params.append('sim_id', simId)
        if (clientId) params.append('client_id', clientId)
        if (recordType) params.append('record_type', recordType)
        if (search) params.append('search', search)
        if (startDate) params.append('start_date', startDate)
        if (endDate) params.append('end_date', endDate)
        if (page) params.append('page', page)
        if (perPage) params.append('per_page', perPage)
        if (imsiGroupId) params.append('imsi_group_id', imsiGroupId)

        const url = `${BASE_URL}/home/get_traffic_details_api?${params.toString()}`
        const result = await axios.get(url, { headers: getHeaders(token) })
        return result.data
    } catch (err) {
        log("Error in getTrafficDetails")
        error.value = err.message
        return { error: true, message: "Eroare la getTrafficDetails", records: [] }
    }
}

export const getTrafficStats = async (token, { clientId, period, imsiGroupId } = {}) => {
    log("welcome in getTrafficStats")
    if (!token || token === "") return { error: true, message: "No token provided", stats: {} }

    try {
        const params = new URLSearchParams()
        if (clientId) params.append('client_id', clientId)
        if (period) params.append('period', period)
        if (imsiGroupId) params.append('imsi_group_id', imsiGroupId)

        const url = `${BASE_URL}/home/get_traffic_stats_api?${params.toString()}`
        const result = await axios.get(url, { headers: getHeaders(token) })
        return result.data
    } catch (err) {
        log("Error in getTrafficStats")
        error.value = err.message
        return { error: true, message: "Eroare la getTrafficStats", stats: {} }
    }
}

export const exportTrafficReport = async (token, { simId, clientId, startDate, endDate, formatType } = {}) => {
    log("welcome in exportTrafficReport")
    if (!token || token === "") return { error: true, message: "No token provided" }

    try {
        const params = new URLSearchParams()
        if (simId) params.append('sim_id', simId)
        if (clientId) params.append('client_id', clientId)
        if (startDate) params.append('start_date', startDate)
        if (endDate) params.append('end_date', endDate)
        if (formatType) params.append('format_type', formatType)

        const url = `${BASE_URL}/home/export_traffic_report_api?${params.toString()}`
        const response = await axios.get(url, {
            headers: getHeaders(token),
            responseType: 'blob'
        })

        // Trigger download
        const blob = new Blob([response.data], { type: 'text/csv' })
        const link = document.createElement('a')
        link.href = URL.createObjectURL(blob)
        link.download = `traffic_report_${startDate || 'all'}_${endDate || 'all'}.csv`
        link.click()
        URL.revokeObjectURL(link.href)

        return { error: false, message: "Export started" }
    } catch (err) {
        log("Error in exportTrafficReport")
        error.value = err.message
        return { error: true, message: "Eroare la exportTrafficReport" }
    }
}

export const importTrafficData = async (token, { simId, fromDate, toDate } = {}) => {
    log("welcome in importTrafficData")
    if (!token || token === "") return { error: true, message: "No token provided" }

    try {
        const url = `${BASE_URL}/home/import_traffic_data_api`
        const payload = {}
        if (simId) payload.sim_id = simId
        if (fromDate) payload.from_date = fromDate
        if (toDate) payload.to_date = toDate

        const result = await axios.post(url, payload, { headers: getHeaders(token) })
        return result.data
    } catch (err) {
        log("Error in importTrafficData")
        error.value = err.message
        return { error: true, message: "Eroare la importTrafficData" }
    }
}

export const getTrafficUsageComparison = async (token, { simId, period, startDate, endDate } = {}) => {
    log("welcome in getTrafficUsageComparison")
    if (!token || token === "") return { error: true, message: "No token provided" }

    try {
        const params = new URLSearchParams()
        if (simId) params.append('sim_id', simId)
        if (period) params.append('period', period)
        if (startDate) params.append('start_date', startDate)
        if (endDate) params.append('end_date', endDate)

        const url = `${BASE_URL}/home/get_traffic_usage_comparison_api?${params.toString()}`
        const result = await axios.get(url, { headers: getHeaders(token) })
        return result.data
    } catch (err) {
        log("Error in getTrafficUsageComparison")
        error.value = err.message
        return { error: true, message: "Eroare la getTrafficUsageComparison" }
    }
}

export const getTrafficLastImport = async (token, { simId } = {}) => {
    log("welcome in getTrafficLastImport")
    if (!token || token === "") return { error: true, message: "No token provided" }

    try {
        const params = new URLSearchParams()
        if (simId) params.append('sim_id', simId)

        const url = `${BASE_URL}/home/get_traffic_last_import_api?${params.toString()}`
        const result = await axios.get(url, { headers: getHeaders(token) })
        return result.data
    } catch (err) {
        log("Error in getTrafficLastImport")
        error.value = err.message
        return { error: true, message: "Eroare la getTrafficLastImport" }
    }
}
